import { Shape, ShapeType } from '@/services/shape';
import { useLayerStore } from '@/store/use-layer-store';
import { emitter, EventType } from '@/utils/emitter.utils';
import { LayerUtils } from '@/utils/shape/layer.util';
import { pickFile } from '@/utils/fs/pick-file.utils';
import { useEditorStore } from '@/store/use-editor-store';
import { useWatchfaceStore } from '@/store/use-watchface-store';
import { ElectronApi } from '@/services/electron.api';
import { cloneDeep, round } from 'lodash-es';
import KonvaService from '@/services/konva';
import { SlotAttribute } from '@/services/shape/attributes/slot';
import { ResourceKey } from '@/services/shape/attributes';
import generateID from '@/utils/generate-id';
import { calculateBarWidth } from '@/utils/calculate-barWidth';
import { useImagesStore } from '@/store/use-images-store';
import { ProgressBarAttribute } from '@/services/shape/attributes/progress-bar';
import { TemplateService } from '@/services/template.service';
import { jumpAppList } from './use-jump-app';
import { useWatchfaceImageValid } from './use-watchface-image-valid';
import { GlobalConfig } from '@/services/gloabl.config';
import { Modal } from 'ant-design-vue';
import { canUse } from '@/utils/can-use.utils';
import { useIFTTTStore } from '@/store/use-ifttt-store';
import { useShapeCount } from './use-shape-count';
import { useSlotFollow } from './use-slot-follow';
import { WatchfaceDeviceType } from './use-watchface-devices';

export function useShapeManage() {
  const layerStore = useLayerStore();
  const editorStore = useEditorStore();
  const watchinfo = useWatchfaceStore();
  const imagesStore = useImagesStore();
  const iftttStore = useIFTTTStore();
  const { validImageSize } = useWatchfaceImageValid();
  const { validShapeCount } = useShapeCount();
  const {
    judgeIsSupportSlotFollowing,
    findParentSlotGroupName,
    deleteFollowingSlotAttr
  } = useSlotFollow();
  const updateShapes = (event: EventType) => {
    Shape.clearCache();
    emitter.emit(event);
  };

  const resetSlotWidgetToDisplay = (layer: StoreLayerItem) => {
    if (layer.type == ShapeType.Slot) {
      const widgetIdToDisplay = layer.attrs.widgetIdToDisplay;
      if (!widgetIdToDisplay) return;
      const item = layer.children?.find(
        (item) => item.attrs._cloneFromId == widgetIdToDisplay
      );

      if (item) layer.attrs.widgetIdToDisplay = item.id;
    }
  };

  /**
   * 复制图层
   * @param shapeId
   * @param themeId
   */
  const copyShape = (
    layer: StoreLayerItem,
    themeId: string,
    withName = false,
    parentId?: string,
    index?: number
  ): StoreLayerItem => {
    const newLayer = cloneDeep(layer);
    if (newLayer.attrs.followingGroupName) {
      delete newLayer.attrs.followingGroupName;
    }
    const _setId = (layer: StoreLayerItem, withName = false) => {
      // 先保存旧id _cloneFromId，后续用于判断来着哪个图层
      layer.attrs._cloneFromId = layer.id;
      layer.id = generateID();
      if (withName) {
        layer.attrs.name += '_copy';
      }
      if (layer.children) {
        layer.children.forEach((item) => _setId(item));
      }
    };
    _setId(newLayer, withName);

    if (parentId) {
      const service = KonvaService.getIns();
      const { x = 0, y = 0 } = service?.getClientRect(parentId) ?? {};
      newLayer.attrs.x -= x;
      newLayer.attrs.y -= y;
    }

    // 重置slot的widgetIdToDisplay
    resetSlotWidgetToDisplay(newLayer);
    if (judgeIsSupportSlotFollowing() && newLayer.type == ShapeType.Slot) {
      LayerUtils.resetResourceColor(newLayer, [editorStore.themeColorId]);
      newLayer.attrs.slotFollowingId = layer.id;
      findParentSlotGroupName(layer.id);
    }

    layerStore.addLayer({
      layer: newLayer,
      themeId,
      parentId,
      index
    });
    updateShapes(EventType['shape:copy']);
    editorStore.select([newLayer.id]);

    return newLayer;
  };

  /**
   * 图层移动操作
   * @param shapeId 待移动图层id
   * @param parentId 可选，目标父元素id, 不传默认移动至 root层
   * @param targetIndex 可选，目标位置索引，默认为 0
   */
  const moveShape = (
    shapeId: string,
    themeId: string,
    parentId?: string,
    targetIndex = 0
  ) => {
    const { layer: oldLayer, index: oldIndex } =
      layerStore.findLayer(shapeId) ?? {};
    if (!oldLayer || oldIndex === undefined) {
      throw new Error(`图层：${shapeId} 不存在`);
    }

    // if (oldLayer.type !== ShapeType.Widget && oldParent?.id != parentId) {
    //   throw new Error(`非widget不允许跨层移动`);
    // }

    const shape = Shape.getShape(shapeId);
    const preParentId = shape?.parent?.id;

    const newLayer = layerStore.moveLayer(
      shapeId,
      themeId,
      parentId,
      targetIndex
    );
    if (!newLayer) {
      throw new Error(`图层：${shapeId} 移动至 ${parentId} 失败`);
    }

    // 当移动到widget中时，需要重置widget的大小
    // const parentShape = Shape.getShape(parentId ?? '');
    // if (parentShape?.type == ShapeType.Widget) {
    //   resizeWidgetByChildren(parentShape);
    // }
    // if (parentShapeOld?.type == ShapeType.Widget) {
    //   resizeWidgetByChildren(parentShapeOld);
    // }

    // 如果是移入到父容器下，将图层的ifttt功能去掉
    if (parentId) {
      iftttStore.deleteIFTTT(themeId, { shapeId });
      // 清除组的条件切换动效
      const _shape = Shape.getShape(shapeId);
      if (_shape?.type === ShapeType.Widget && (_shape.attrs as any).widgetConditions?.length) {
        _shape.setAttrs({ widgetConditions: undefined, widgetAnimations: undefined });
      }
    }

    // 移出后，如果原父组有条件切换动效且子元素不足2个，清除动效
    if (preParentId) {
      const preParentShape = Shape.getShape(preParentId);
      if (
        preParentShape?.type === ShapeType.Widget &&
        (preParentShape.attrs as any).widgetConditions?.length > 0 &&
        (preParentShape.children?.length ?? 0) <= 1
      ) {
        preParentShape.setAttrs({ widgetConditions: undefined, widgetAnimations: undefined });
      }
    }

    shape?.initAttrs({ attrs: [] });
    emitter.emit(EventType['shape:drag'], { parentId, shapeId, preParentId, isSameParentReorder: preParentId === parentId || (!preParentId && !parentId) });
  };

  /**
   * @description 求最大深度
   * @param shape Shape
   * @returns number
   */
  const maxDeepOfShape = (shape?: Shape): number => {
    if (!shape) return 0;
    if (!shape.children || shape.children.length <= 0) return 1;
    return (
      Math.max(
        ...(shape.children?.map((item: Shape) => maxDeepOfShape(item)) ?? [])
      ) + 1
    );
  };

  /**
   * @description 寻找最顶端的组节点
   * @param shape Shape
   * @returns
   */
  const findMostTopGroup = (shape?: Shape): Shape | undefined => {
    if (!shape) return shape;
    let parent: Shape | undefined = shape;
    while (
      parent?.parent &&
      [ShapeType.Group, ShapeType.Widget].includes(parent.parent.type)
    ) {
      parent = parent.parent;
    }
    return parent;
  };

  const widgeted = (shapeIds: string[], themeId: string, parentId?: string) => {
    // 无论是移入还是移除组，都将图层的ifttt功能去掉
    shapeIds.forEach((shapeId) => {
      iftttStore.deleteIFTTT(themeId, { shapeId });
      // 清除组的条件切换动效
      const shape = Shape.getShape(shapeId);
      if (shape?.type === ShapeType.Widget && shape.attrs.widgetConditions?.length) {
        shape.setAttrs({ widgetConditions: [], widgetAnimations: [] });
      }
    });

    const widgetId = generateID();
    let newLayer: StoreLayerItem = {
      id: widgetId,
      type: ShapeType.Widget,
      children: [],
      attrs: {
        name: '',
        x: 0,
        y: 0,
        _isLocked: false,
        _visibility: true
      }
    };
    if (
      shapeIds.some(
        (id) =>
          maxDeepOfShape(findMostTopGroup(Shape.getShape(id))) >=
          GlobalConfig.groupInletDeep
      )
    ) {
      Modal.warning({
        title: '无法成组',
        content: `组嵌套最多支持${GlobalConfig.groupInletDeep}层！`,
        okText: '知道了'
      });
      return;
    }
    newLayer = layerStore.group(shapeIds, newLayer, themeId, parentId);
    const widget = Shape.getShape(widgetId);
    if (widget?.children?.length) {
      for (let i = 0; i < widget?.children.length; i++) {
        const appName = widget.children[i].sourceItem?.jumpAppName;
        if (appName) {
          const jumpAppItem = jumpAppList.find((item) => appName === item.name);
          widget?.setAttrs({ jumpApp: jumpAppItem?.name });
          break;
        }
      }
    }

    updateShapes(EventType['shape:widgeted']);
    editorStore.updateLayerTreeExpandkeys(newLayer.id, 'expand');
    editorStore.select([newLayer.id]);
  };

  const group = (shapeIds: string[], themeId: string, parentId?: string) => {
    let newLayer: StoreLayerItem = {
      id: generateID(),
      type: ShapeType.Group,
      children: [],
      attrs: {
        name: '',
        x: 0,
        y: 0,
        _isLocked: false,
        _visibility: true
      }
    };
    newLayer = layerStore.group(shapeIds, newLayer, themeId, parentId);
    updateShapes(EventType['shape:widgeted']);
    editorStore.select([newLayer.id]);
  };

  const unWidgeted = (widgetId: string, themeId: string) => {
    const { layer } = layerStore.findLayer(widgetId) || {};
    if (!layer) return;
    let newLayers: StoreLayerItem[] = [];
    if (layer.children) {
      newLayers = layer.children.map((item) => {
        LayerUtils.setAbstractAttrs(item, {
          x: layer.attrs.x,
          y: layer.attrs.y
        });
        return item;
      });
    }
    layerStore.ungroup(widgetId, newLayers, themeId);
    updateShapes(EventType['shape:unwidgeted']);
    editorStore.unselect(widgetId);
  };
  const unGroup = (widgetId: string, themeId: string) => {
    const { layer } = layerStore.findLayer(widgetId) || {};
    if (!layer) return;
    let newLayers: StoreLayerItem[] = [];
    if (layer.children) {
      newLayers = layer.children.map((item) => {
        LayerUtils.setAbstractAttrs(item, {
          x: layer.attrs.x,
          y: layer.attrs.y
        });
        return item;
      });
    }
    layerStore.ungroup(widgetId, newLayers, themeId);
    updateShapes(EventType['shape:unwidgeted']);
    editorStore.unselect(widgetId);
  };

  const getWidgetMaxCount = (deviceType: WatchfaceDeviceType): number => {
    if (ElectronApi.isNormal()) {
      return GlobalConfig.slotChildrenMaxCount;
    }

    if (['P62', 'P67', 'Q61', 'Q63'].includes(deviceType)) {
      return 128;
    }

    if (deviceType === 'O61' || deviceType === 'Q66') {
      return 100;
    }

    return GlobalConfig.slotChildrenMaxCount;
  };

  const sloted = (
    widgetIds: string[],
    themeId: string,
    slotFollowingId?: string
  ) => {
    const slotId = generateID();
    // 容器基础属性
    const attrs = new SlotAttribute();
    let newLayer: StoreLayerItem = {
      id: slotId,
      type: ShapeType.Slot,
      children: [],
      attrs: {
        ...attrs,
        name: '',
        x: 0,
        y: 0,
        _isLocked: false,
        _visibility: true,
        sameEditBox: canUse('slot-border-image').valid ? true : false,
        slotFollowingId
      }
    };
    if (slotFollowingId) {
      findParentSlotGroupName(slotFollowingId);
    }

    const maxWidgetCounts = getWidgetMaxCount(watchinfo.deviceType);

    if (widgetIds.length > maxWidgetCounts) {
      Modal.warning({
        title: '无法成容器',
        content: `最多支持${maxWidgetCounts}个组成容器，请您调整后再成容器。`,
        okText: '知道了'
      });
      return;
    }

    // 成容器时清除子元素的动效
    widgetIds.forEach((id) => {
      iftttStore.deleteIFTTT(themeId, { shapeId: id });
      const shape = Shape.getShape(id);
      if (shape?.type === ShapeType.Widget && shape.attrs.widgetConditions?.length) {
        shape.setAttrs({ widgetConditions: [], widgetAnimations: [] });
      }
    });

    newLayer = layerStore.group(widgetIds, newLayer, themeId);

    // 添加组默认显示数据项id
    newLayer.attrs.widgetIdToDisplay = newLayer.children?.[0]?.id;

    updateShapes(EventType['shape:sloted']);
    editorStore.select([newLayer.id]);
    editorStore.updateLayerTreeExpandkeys(newLayer.id, 'expand');
  };

  const unSloted = (slotId: string, themeId: string) => {
    const { layer } = layerStore.findLayer(slotId) || {};
    if (!layer) return;
    let newLayers: StoreLayerItem[] = [];
    if (layer.children) {
      newLayers = layer.children.map((item) => {
        LayerUtils.setAbstractAttrs(item, {
          x: layer.attrs.x,
          y: layer.attrs.y
        });
        return item;
      });
    }
    layerStore.ungroup(slotId, newLayers, themeId);
    if (layer.attrs.followingGroupName) {
      deleteFollowingSlotAttr(slotId);
    }
    updateShapes(EventType['shape:unsloted']);
    editorStore.unselect(slotId);
  };

  // 验证shapeCount

  const addShape = async (
    type: ShapeType,
    data: {
      attrs?: Shape['attrs'];
      cloneFrom?: StoreLayerItem;
      // 是否需要先上传图片，然后在添加shape
      uploadImageBefore?: ResourceKey;
    }
  ) => {
    const path = ElectronApi.path;
    const theme = editorStore.theme;
    if (!theme || !path) return;
    if (!(await validShapeCount())) return;

    const { attrs, cloneFrom, uploadImageBefore } = data;

    // 选择图片（新增之前）
    let image: undefined | string;
    const _pickImage = async () => {
      const image = await pickFile({ extensions: ['png'] }).then((r) => r?.[0]);
      if (!image) return;
      const valid = await validImageSize(image);
      if (valid) {
        return image;
      }
    };
    if (!cloneFrom && !!uploadImageBefore) {
      image = await _pickImage();
      if (!image) return;
    }
    // 新增shape
    const layer = cloneFrom ? cloneDeep(cloneFrom) : undefined;
    if (layer) {
      LayerUtils.regenerateId(layer);
      LayerUtils.resetResourceColor(layer, [editorStore.themeColorId]);
      // 如果是容器，需要默认展示最后一个child
      if (layer.type == ShapeType.Slot) {
        layer.attrs.widgetIdToDisplay =
          layer.children?.[layer.children?.length - 1]?.id;
      }
    }
    const shape = Shape.create(type, {
      attrs: {
        ...attrs,
        x: attrs?.x ?? round(watchinfo.size.width / 2 - (attrs?.w ?? 100) / 2),
        y: attrs?.y ?? round(watchinfo.size.height / 2 - (attrs?.h ?? 100) / 2)
      },
      layer
    });

    // 新增之后，添加资源
    if (image && uploadImageBefore) {
      const colorId = editorStore.themeColor?.id ?? '';
      // 使用图片作为名称
      shape.setAttrs({
        name: path.basename(image).split('.')[0]
      });

      // 上传图片，并将图片的宽度设置为图层的宽度
      await shape.uploadResource(
        {
          resourceName: uploadImageBefore,
          colorGroup: colorId,
          realpath: image
        },
        true,
        true
      );
      // 如果是进度条，设置进度条半径和宽度
      // 此时图片的宽度为图层的宽度，需要将图层的宽度设置为弧形半径的
      if (type == ShapeType.ProgressBar) {
        const { w = 0, h = 0 } = shape.attrs;
        const r = (shape.attrs.w ?? 0) / 2;
        const progressBarType: ProgressBarAttribute['progressBarType'] =
          w == h ? 'arc' : 'line';

        if (progressBarType == 'arc') {
          try {
            const _images = shape.getResources({
              resourceName: 'progress',
              colorGroup: colorId,
              base64: true
            });
            const {
              width = 0,
              angleEnd = 360,
              angleStart = 0
            } = (await calculateBarWidth(_images[0])) ?? {};

            const _innerRingPercent = Math.ceil(((r - width) / r) * 100);
            const innerRingRadius = Math.floor((r * _innerRingPercent) / 100);
            const barWidth = r - innerRingRadius;
            const barRadius = innerRingRadius + barWidth / 2;

            shape.setAttrs({
              progressBarType: 'arc',
              growDirection: 'positive',
              barWidth,
              barRadius,
              angleStart: Math.floor(angleStart),
              angleEnd: Math.ceil(angleEnd)
            });
          } catch (e) {
            console.error(e);
            shape.setAttrs({
              progressBarType: 'arc',
              growDirection: 'positive'
            });
          }
        } else {
          shape.setAttrs({
            progressBarType: 'line',
            growDirection: 'to-right'
          });
        }
      }
    }

    if (shape.type == ShapeType.ImageArray) {
      editorStore.autoOpenDataSourceShapeId = shape?.id ?? '';
    }
    return shape;
  };

  /**
   * 画布居中元素
   */
  const toCenter = (shape: Shape) => {
    shape.setAttrs({
      x: round(watchinfo.size.width / 2 - (shape.attrs.w ?? 0) / 2),
      y: round(watchinfo.size.height / 2 - (shape.attrs.h ?? 0) / 2)
    });
  };

  const addTemplate = async (templateId: string) => {
    const data = await new TemplateService().parseTemplateZip(templateId);
    if (!data) return;
    const { layers, resources } = data;
    const theme = editorStore.theme;
    const themeColorId = editorStore.themeColorId;
    if (!theme || !themeColorId) return;

    // 1. 为每个资源添加一个前缀，防止和已有的冲突
    // 2. 重置layers中的配色为当前配色
    // 3. 重置layer id，会在addShape中操作
    const template_ = 'template_';
    const stack = [...layers];
    while (stack.length) {
      const layer = stack.shift();
      if (layer?.children?.length) stack.push(...layer.children);
      const names = Object.keys(layer?.attrs.resources ?? {});
      names.forEach((name) => {
        const srcValues = Object.values(layer?.attrs.resources?.[name] ?? {});
        if (layer?.attrs.resources && srcValues.length) {
          layer.attrs.resources[name] = {
            [themeColorId]: srcValues?.[0].map((item) => template_ + item) ?? []
          };
        }
      });
    }
    // 开始依次添加layer
    for (const layer of layers) {
      await addShape(layer.type as ShapeType, { cloneFrom: layer });
    }
    // 添加resources
    resources.forEach((item) => {
      item.basename = template_ + item.basename;
      imagesStore.insertImage(item);
    });
  };

  /**
   * 根据子元素显示区域，重置父元素的大小和宽度
   * @param parentShape
   * @returns
   */
  const resizeWidgetByChildren = (shape: Shape) => {
    const layer = shape.layer;
    if (!layer || !shape.children.length) return;

    const isInSlot = shape.parent?.type == ShapeType.Slot;

    const { x: x0 = 0, y: y0 = 0 } = shape.attrs;
    const { x, y, w, h } = LayerUtils.calcLayersRect(layer.children ?? []);
    const offsetX = x - x0;
    const offsetY = y - y0;

    if (!isInSlot) {
      layer.children?.forEach((_layer) => {
        LayerUtils.setRelativeAttrs(_layer, { x: offsetX, y: offsetY });
      });
      Object.assign(layer.attrs, { x, y });
    }
    Object.assign(layer.attrs, { w, h });
    [layer, ...(layer.children ?? [])].forEach((_layer) => {
      Shape.getShape(_layer.id)?.initAttrs({ attrs: ['x', 'y', 'w', 'h'] });
    });
  };

  return {
    copyShape,
    moveShape,
    widgeted,
    unWidgeted,
    sloted,
    unSloted,
    addShape,
    group,
    unGroup,
    addTemplate,
    toCenter,
    resizeWidgetByChildren,
    maxDeepOfShape,
    findMostTopGroup,
    resetSlotWidgetToDisplay,
    getWidgetMaxCount
  };
}
